import { client } from "@/hono";
import { AddToCalculationModal } from "@/modals/partners/add-to-calculation-modal";
import { BlankSlateInline } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { DataGrid } from "@msuite/ui/data-grid";
import { useDisclosure } from "@msuite/ui/hooks";
import { ErrorIcon, Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { Spinner } from "@msuite/ui/spinner";
import { Tag } from "@msuite/ui/tag";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import {
	getCalculationStateColor,
	getCalculationStateTranslationKey,
} from "@msuite/utils/calculations";
import { formatNumber } from "@msuite/utils/numbers";
import type { Tables } from "@msuite/utils/types";
import React from "react";
import { PartnerTimelineAssignmentReferenceServiceCalculationMenu } from "./partner-timeline-assignment-reference-service-calculation-menu";

/** Props Interface */
interface PartnerTimelineAssignmentReferenceServiceCalculationProps {
	referenceId: string;
	partnershipId: string;
	reference: Tables<"ticketAssignmentReferences">;
	hostOrgId: string;
}

export function PartnerTimelineAssignmentReferenceServiceCalculation({
	referenceId,
	reference,
	partnershipId,
	hostOrgId,
}: PartnerTimelineAssignmentReferenceServiceCalculationProps) {
	/** Hooks */
	const { t } = useTranslation();
	const addToCalculationModal = useDisclosure();

	/** Query */
	const {
		data: calculation,
		isLoading,
		refetch,
		isError,
	} = useQuery({
		queryKey: ["parsc", referenceId],
		queryFn: async () => {
			const response = await client.calculations.parsc[":referenceId"].$get({
				param: { referenceId },
			});
			const data = await responseHandler(response);
			return data.calculation;
		},
		staleTime: 1_000,
	});

	/** Functions */
	function handleOnNavigateToCalculationInitialization() {
		const currentUrl = new URL(window.location.href);
		const encodedUrl = encodeURIComponent(`${currentUrl.pathname}${currentUrl.search}`);
		let url = `/calculations/init?type=parsc&identifier=${referenceId}`;
		url += `&returnTo=${encodedUrl}`;
		window.open(url, "_blank");
	}

	function handleOnNavigateToCalculationEditor() {
		if (!calculation?.id) return;
		const currentUrl = new URL(window.location.href);
		const encodedUrl = encodeURIComponent(`${currentUrl.pathname}${currentUrl.search}`);
		let url = `/calculations/${calculation?.serviceCalculationId}/editor?type=parsc&identifier=${referenceId}`;
		url += `&returnTo=${encodedUrl}`;
		window.open(url, "_blank");
	}

	function handleOnNavigateToCalculation() {
		window.open(`/calculations/${calculation?._serviceCalculation.id}`, "_blank");
	}

	async function handleOnAddReferenceToCalculation(calculationId: string) {
		await client.calculations[":calculationId"].references.$post({
			param: { calculationId },
			json: { referenceId },
		});
		await refetch();
	}

	async function handleOnDeleteCalculationReference() {
		await client.calculations[":calculationId"].references[":referenceId"].$delete({
			param: { calculationId: calculation!._serviceCalculation.id!, referenceId },
		});
		await refetch();
	}

	/** Guard */
	if (isLoading)
		return (
			<BlankSlateInline
				Icon={() => <Spinner />}
				label={t("general.loading")}
			/>
		);

	if (isError)
		return (
			<BlankSlateInline
				Icon={() => <ErrorIcon />}
				label={t("general.error")}
			/>
		);

	if (calculation) {
		return (
			<React.Fragment>
				<DataGrid className="col-start-2">
					<DataGrid.Row>
						<DataGrid.Label>{t("calculations.calculation")}</DataGrid.Label>
						<DataGrid.Cell className="h-stack justify-between">
							<p>{calculation._serviceCalculation.name}</p>
							<PartnerTimelineAssignmentReferenceServiceCalculationMenu
								handleOnOpen={handleOnNavigateToCalculation}
								handleOnEdit={handleOnNavigateToCalculationEditor}
								handleOnDelete={handleOnDeleteCalculationReference}
								referenceId={referenceId}
								partnershipId={partnershipId}
								reference={reference}
								hostOrgId={hostOrgId}
							/>
						</DataGrid.Cell>
					</DataGrid.Row>
					<DataGrid.Row>
						<DataGrid.Label>{t("general.state")}</DataGrid.Label>
						<DataGrid.Value className="tabular-nums">
							<Tag
								color={getCalculationStateColor(calculation._serviceCalculation.state)}
								variant="plain"
								size="md"
							>
								{t(getCalculationStateTranslationKey(calculation._serviceCalculation.state))}
							</Tag>
						</DataGrid.Value>
					</DataGrid.Row>
					<DataGrid.Row>
						<DataGrid.Label>{t("organization.data.services")}</DataGrid.Label>
						<DataGrid.Value className="tabular-nums">
							{guardArray(calculation._serviceCalculation._serviceCalculationItems).length}
						</DataGrid.Value>
					</DataGrid.Row>
					<DataGrid.Row>
						<DataGrid.Label>{t("invoices.sum_net")}</DataGrid.Label>
						<DataGrid.Value className="tabular-nums">
							{formatNumber(calculation._serviceCalculation.total, "currency")}
						</DataGrid.Value>
					</DataGrid.Row>
				</DataGrid>
			</React.Fragment>
		);
	}

	/** Render */
	return (
		<React.Fragment>
			<div className="col-start-1 col-span-2 h-stack">
				<Button
					onClick={handleOnNavigateToCalculationInitialization}
					icon={
						<Icon
							name="Grid2x2Plus"
							className="stroke-emerald-500 fill-emerald-500/10"
						/>
					}
				>
					{t("calculations.create_calculation")}
				</Button>
				<Button
					onClick={addToCalculationModal.onOpen}
					icon={
						<Icon
							name="Link"
							className="stroke-accent-500 fill-accent-500/10"
						/>
					}
				>
					{t("calculations.add_to_calculation")}
				</Button>
			</div>
			<AddToCalculationModal
				isOpen={addToCalculationModal.isOpen}
				onClose={addToCalculationModal.onClose}
				referenceId={referenceId}
				onOpenCreateCalculation={handleOnNavigateToCalculationInitialization}
				onComplete={handleOnAddReferenceToCalculation}
			/>
		</React.Fragment>
	);
}
